import { InfoIcon } from 'lucide-react-native';
import React, { type ReactNode } from 'react';
import type { GestureResponderEvent } from 'react-native';
import {
    isWeb,
    type StackProps,
    type TextProps,
    Tooltip,
    View,
    XStack,
    type XStackProps,
    YStack,
} from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';

import { isEmpty } from '../../misc';
import {
    type ColorTokenKey,
    useThemeColors,
} from '../../themes/SmoothieColorTokens';
import { fontTokens } from '../../themes/SmoothieFontTokens';
import { borderCurve, hitSlop } from '../componentHelpers';
import { Icon, type IconType } from '../icons/Icon';
import { Shadows } from '../Shadows';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
} from '../text/Typography';

interface IconProps {
    icon: ReactNode | IconType;
    color: ColorTokenKey;
    size: number;
    fill?: ColorTokenKey;
}

const ButtonIcon = ({ icon, color, size, fill }: IconProps) => {
    // If it's already a React element (JSX), render it directly
    if (icon != null && React.isValidElement(icon)) {
        return (
            <View alignItems="center" justifyContent="center">
                {icon}
            </View>
        );
    }

    return (
        <View alignItems="center" justifyContent="center">
            <Icon
                icon={icon as IconType}
                color={color}
                size={size}
                fill={fill}
            />
        </View>
    );
};

export enum ButtonVariant {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    SECONDARY_ALT = 'secondary-alt',
    TERTIARY = 'tertiary',
    PREMIUM = 'premium',
    PREMIUM_SECONDARY = 'premium-secondary',
    PREMIUM_TERTIARY = 'premium-tertiary',
}

export enum ButtonSize {
    LARGE = 'large',
    SMALL = 'small',
    TINY = 'tiny',
}

type Tone = 'neutral' | 'positive' | 'danger';

const height: Record<ButtonSize, number> = {
    [ButtonSize.LARGE]: 56,
    [ButtonSize.SMALL]: 40,
    [ButtonSize.TINY]: 32,
};

const widthMode: Record<'full' | 'auto', '100%' | 'auto'> = {
    full: '100%',
    auto: 'auto',
};

const paddingHorizontal: Record<ButtonSize, number> = {
    [ButtonSize.LARGE]: 16,
    [ButtonSize.SMALL]: 16,
    [ButtonSize.TINY]: 14,
};

const iconSize: Record<ButtonSize, number> = {
    [ButtonSize.LARGE]: 18,
    [ButtonSize.SMALL]: 16,
    [ButtonSize.TINY]: 14,
};

const borderRadius: Record<ButtonSize, number> = {
    [ButtonSize.LARGE]: 16,
    [ButtonSize.SMALL]: 12,
    [ButtonSize.TINY]: 12,
};

const direction: Record<'left' | 'right', 'row' | 'row-reverse'> = {
    left: 'row',
    right: 'row-reverse',
};

const TERTIARY_BACKGROUND_HOVER_COLOR: Record<Tone, ColorTokenKey> = {
    neutral: '$gray100',
    positive: '$green100',
    danger: '$red100',
};

// Translucent so it reads as a very light peach on light surfaces and as a
// soft tint on the dark gold Premium surfaces, without hiding light text.
const PREMIUM_TERTIARY_BACKGROUND_HOVER_COLOR = 'rgba(242, 216, 184, 0.16)';

const isTertiaryVariant = (variant: ButtonVariant) =>
    variant === ButtonVariant.TERTIARY ||
    variant === ButtonVariant.PREMIUM_TERTIARY;

const getBackgroundColor = (
    variant: ButtonVariant,
    tone?: Tone,
    disabled?: boolean,
): ColorTokenKey | 'transparent' => {
    if (isTertiaryVariant(variant)) return 'transparent';
    if (disabled) return '$gray400';
    if (variant === ButtonVariant.PRIMARY) {
        return tone === 'positive'
            ? '$green900'
            : tone === 'danger'
              ? '$red900'
              : '$black';
    }
    if (variant === ButtonVariant.PREMIUM) return 'transparent';
    if (variant === ButtonVariant.PREMIUM_SECONDARY) return '$peachDarkest';
    return tone === 'positive'
        ? '$green300'
        : tone === 'danger'
          ? '$red300'
          : variant === ButtonVariant.SECONDARY_ALT
            ? '$white'
            : '$gray200';
};

const getTextColor = (
    variant: ButtonVariant,
    tone?: Tone,
    disabled?: boolean,
): ColorTokenKey => {
    if (disabled) return '$gray900';
    if (variant === ButtonVariant.PRIMARY) return '$white';
    if (
        variant === ButtonVariant.PREMIUM ||
        variant === ButtonVariant.PREMIUM_TERTIARY
    ) {
        return '$peachDarkest';
    }
    if (variant === ButtonVariant.PREMIUM_SECONDARY) {
        return '$peachAccentContrast';
    }

    return tone === 'positive'
        ? '$green1100'
        : tone === 'danger'
          ? '$red1100'
          : '$black';
};

// TODO pick specific StackProps to support... testID, margin...
interface ButtonProps extends Omit<BaseButtonProps, 'size' | 'text'> {
    text: string;
    size?: ButtonSize.LARGE | ButtonSize.SMALL | ButtonSize.TINY;
}

/**
 * Our standard, main button component. Use this for most button needs.
 * For alternate button configurations, see PillButton, IconButton, & InfoButton
 */
export const Button = ({ size = ButtonSize.LARGE, ...props }: ButtonProps) => {
    return <BaseButton size={size} {...props} />;
};

interface PillButtonProps {
    variant?: 'default' | 'default-alt' | 'tertiary' | 'primary';
    text?: string;
    icon?: IconType;
    iconPosition?: 'left' | 'right';
    onPress: () => void;
    pillProps?: XStackProps;
    textProps?: TextProps;
    disabled?: boolean;
}

/**
 * An accessory button used for detailed actions on a screen.
 * TBD whether this remains a fixture in our latest Design System definition.
 */
export const PillButton = ({
    variant = 'default',
    pillProps,
    ...props
}: PillButtonProps) => {
    const buttonVariant =
        variant === 'tertiary'
            ? ButtonVariant.TERTIARY
            : variant === 'primary'
              ? ButtonVariant.PRIMARY
              : variant === 'default-alt'
                ? ButtonVariant.SECONDARY_ALT
                : ButtonVariant.SECONDARY;
    return (
        <BaseButton
            variant={buttonVariant}
            size={ButtonSize.TINY}
            width="auto"
            buttonProps={pillProps}
            {...props}
        />
    );
};

interface IconButtonProps {
    variant: 'primary' | 'secondary' | 'secondary-alt' | 'tertiary';
    size?: 'regular' | 'tiny';
    icon: IconType;
    iconFill?: ColorTokenKey;
    onPress: (e: GestureResponderEvent) => void;
    tone?: Tone;
    disabled?: boolean;
}

/**
 * A button to be used when there is only an icon and no text.
 * Supports all variants and tones of our standard button.
 */
export const IconButton = ({
    tone = 'neutral',
    size = 'regular',
    variant,
    ...props
}: IconButtonProps) => {
    return (
        <BaseIconButton
            tone={tone}
            size={size === 'tiny' ? ButtonSize.TINY : ButtonSize.SMALL}
            variant={variant as ButtonVariant}
            {...props}
        />
    );
};

const BaseIconButton = (props: BaseButtonProps) => {
    const isTiny = props.size === ButtonSize.TINY;

    return (
        <BaseButton
            {...props}
            buttonProps={{
                padding: 8,
                width: isTiny ? 32 : 40,
                height: isTiny ? 32 : 40,
                borderRadius: isTiny ? 12 : 16,
            }}
        />
    );
};

interface InfoButtonProps extends Pick<XStackProps, 'onLayout'> {
    onPress?: (e: GestureResponderEvent) => void;
    text?: string;
    subtle?: boolean;
    disabled?: boolean;
}

/**
 * A button to be used when we want to show more info about something on the screen.
 * Typically opens a sheet/modal. Supports with/without a label, standard or subtle
 */
export const InfoButton = ({
    onPress,
    text,
    subtle,
    disabled,
}: InfoButtonProps) => {
    const variant = ButtonVariant.TERTIARY;
    const subtleColor: ColorTokenKey = '$gray1000';
    const icon = (
        <Icon
            size={subtle ? 16 : 24}
            color={subtle ? subtleColor : getTextColor(variant)}
            icon={InfoIcon}
        />
    );

    if (text == null || text.trim() === '') {
        return (
            <BaseIconButton
                variant={variant}
                icon={icon}
                onPress={onPress}
                size={subtle ? ButtonSize.TINY : ButtonSize.SMALL}
                disabled={disabled}
            />
        );
    }
    return (
        <BaseButton
            text={text}
            variant={variant}
            size={ButtonSize.SMALL}
            width="auto"
            icon={icon}
            iconPosition="right"
            disabled={disabled}
            onPress={onPress}
            {...(subtle && {
                textProps: {
                    fontWeight: fontTokens.body.weight[1],
                    color: subtleColor,
                },
            })}
        />
    );
};

interface TabButtonProps {
    text: string;
    icon: ReactNode;
    isSelected: boolean;
    onPress: () => void;
}
/**
 * A style of selectable button, currently unique to the SideNav component
 */
export const TabButton = ({
    text,
    icon,
    isSelected,
    onPress,
}: TabButtonProps) => {
    const variant = ButtonVariant.TERTIARY;
    const size = ButtonSize.LARGE;
    const fontWeight = isSelected
        ? fontTokens.body.weight[2]
        : fontTokens.body.weight[1];

    return (
        <Tooltip groupId="tab-button-tooltip" placement="right" delay={0}>
            <Tooltip.Content
                $md={{ display: 'none' }}
                width={110}
                marginLeft={paddingHorizontal[size] * -1}
                backgroundColor={TERTIARY_BACKGROUND_HOVER_COLOR.neutral}
                height={height[size]}
                justifyContent="center"
                alignItems="flex-start"
                borderTopRightRadius={borderRadius[size]}
                borderBottomRightRadius={borderRadius[size]}
                borderTopLeftRadius={0}
                borderBottomLeftRadius={0}
                pointerEvents="none"
            >
                <BodyMediumRegular
                    animation="quicker"
                    enterStyle={{ opacity: 0.5, scale: 0.99 }}
                    color={isSelected ? '$black' : '$gray1100'}
                    fontWeight={fontWeight}
                >
                    {text}
                </BodyMediumRegular>
            </Tooltip.Content>
            <Tooltip.Trigger>
                <BaseButton
                    variant={variant}
                    size={size}
                    text={text}
                    icon={icon}
                    width="full"
                    onPress={onPress}
                    textProps={{
                        paddingLeft: 4,
                        color: isSelected ? '$black' : '$gray900',
                        fontWeight,
                        display: 'none',
                        $md: { display: 'block' },
                        $lg: { paddingLeft: 12 },
                    }}
                    $md={{
                        justifyContent: 'flex-start',
                        paddingHorizontal: 16,
                    }}
                    role="tab"
                    aria-selected={isSelected}
                    aria-label={text}
                    aria-describedby={`Shows the ${text} tab`}
                    animation={undefined} // any animation here would be difficult to sync with the show/hide of the tooltip
                />
            </Tooltip.Trigger>
        </Tooltip>
    );
};

interface BaseButtonProps extends Omit<StackProps, 'width' | 'disabled'> {
    variant: ButtonVariant; // TODO switch to string values?
    tone?: Tone;
    size?: ButtonSize; // TODO switch to string values?
    width?: 'full' | 'auto';
    text?: string;
    icon?: ReactNode | IconType; // Moving away from providing icon as ReactNode
    iconFill?: ColorTokenKey;
    iconPosition?: 'left' | 'right';
    onPress?: (e: GestureResponderEvent) => void;
    buttonProps?: StackProps;
    textProps?: TextProps;
    disabled?: boolean;
}

const PremiumButtonGradient = () => {
    const { getColor } = useThemeColors();
    const colors: [string, string] = [
        getColor('$peachLight'),
        getColor('$peach'),
    ];

    return (
        <YStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={0}
        >
            <LinearGradient
                colors={colors}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                width="100%"
                height="100%"
            />
        </YStack>
    );
};

/**
 * The common, flexible core of our button components. Meant to only be used by approved button components,
 * and not for making new one-off unique/custom buttons everywhere.
 */
const BaseButton = ({
    variant,
    tone = 'neutral',
    size = ButtonSize.LARGE,
    width = 'full',
    text,
    icon,
    iconFill,
    iconPosition = 'left',
    onPress,
    buttonProps,
    textProps,
    disabled = false,
    ...props
}: BaseButtonProps) => {
    const isTertiary = isTertiaryVariant(variant);
    const isLarge = size === ButtonSize.LARGE;
    const isTiny = size === ButtonSize.TINY;
    const needsSpecialPressStyle = isTertiary || isTiny || isEmpty(text);
    const isPremium = variant === ButtonVariant.PREMIUM && !disabled;

    const backgroundColor = getBackgroundColor(variant, tone, disabled);
    const textColor = getTextColor(variant, tone, disabled);

    return (
        <XStack // TODO make this a button
            // disabled={disabled} add this back when this element is a button element again
            key={`${variant}-${disabled}`} // works around this issue: https://github.com/tamagui/tamagui/issues/3055
            onPress={disabled ? undefined : onPress}
            height={height[size]}
            width={widthMode[width]}
            backgroundColor={backgroundColor}
            borderRadius={borderRadius[size]}
            paddingHorizontal={isTertiary ? 8 : paddingHorizontal[size]}
            justifyContent="center"
            alignSelf="center"
            position="relative"
            overflow="hidden"
            userSelect="none"
            cursor={disabled ? 'default' : 'pointer'}
            {...(!disabled && {
                pressStyle: {
                    scale: needsSpecialPressStyle ? 0.96 : 0.98,
                    opacity: 0.87,
                },
            })}
            {...(!isLarge && hitSlop(4))}
            {...(isWeb &&
                !disabled && {
                    hoverStyle: isTertiary
                        ? {
                              backgroundColor:
                                  variant === ButtonVariant.PREMIUM_TERTIARY
                                      ? PREMIUM_TERTIARY_BACKGROUND_HOVER_COLOR
                                      : TERTIARY_BACKGROUND_HOVER_COLOR[tone],
                          }
                        : isTiny
                          ? { ...Shadows.RING_SMALL }
                          : { ...Shadows.RING },
                })}
            animation="button"
            animateOnly={
                isWeb
                    ? ['transform', 'opacity', 'boxShadow']
                    : ['transform', 'opacity']
            }
            {...borderCurve('continuous')}
            {...buttonProps}
            {...props}
        >
            {isPremium && <PremiumButtonGradient />}
            <XStack
                zIndex={1}
                justifyContent="center"
                alignItems="center"
                gap={isTiny ? 6 : 8}
                flexDirection={direction[iconPosition]}
            >
                {icon != null && (
                    <ButtonIcon
                        icon={icon}
                        color={textColor}
                        size={iconSize[size]}
                        fill={iconFill}
                    />
                )}
                {text != null && // TODO handle underline for tertiary?
                    (!isLarge ? (
                        <BodySmallRegular color={textColor} {...textProps}>
                            {text}
                        </BodySmallRegular>
                    ) : (
                        <BodyMediumEmphasized color={textColor} {...textProps}>
                            {text}
                        </BodyMediumEmphasized>
                    ))}
            </XStack>
        </XStack>
    );
};
